import { createSelector } from 'reselect';

import { type PaymentSource, paymentSources } from 'src/constants/taxonomy';
import type { StoreState } from 'src/store/types';
import type { Maybe } from 'src/types';
import { sumMoney } from 'src/utils/money';

import { selectIsAdministrator, selectSeekBillingAccountId } from '../accounts';
import { selectInvoices } from '../invoices';
import { selectFeatures } from '../view';

const MAXIMUM_INVOICES_IN_SINGLE_PAYMENT = 10;

export const selectPaymentInvoiceIds = (state: StoreState) =>
  state.paymentInvoiceIds || [];

export const selectPaymentInvoices = createSelector(
  selectPaymentInvoiceIds,
  selectInvoices,
  (paymentInvoiceIds, invoices) => {
    let currency: Maybe<string> = null;
    return invoices
      .filter((invoice) => paymentInvoiceIds.includes(invoice.invoiceId))
      .filter((invoice) => {
        if (!currency) {
          currency = invoice.currency;
        }
        if (invoice.currency !== currency) {
          // eslint-disable-next-line no-console
          console.warn('Ignoring selected invoice due to cross-currency');
        }
        return invoice.currency === currency;
      });
  },
);

export const selectMultipleInvoicePayment = createSelector(
  selectPaymentInvoices,
  (invoices) => invoices.length > 1,
);

export const selectPaymentSubTotal = createSelector(
  selectPaymentInvoices,
  (invoices) =>
    sumMoney(
      invoices.map((invoice) => ({
        amount: invoice.netPayable || 0,
        currency: invoice.currency,
      })),
    ),
);

export const selectIsPartialPayment = createSelector(
  selectPaymentInvoices,
  (invoices) =>
    !invoices.every(
      (invoice) =>
        invoice.amountPayable && invoice.amount === invoice.amountPayable,
    ),
);

export const selectPaymentTaxTotal = createSelector(
  selectPaymentInvoices,
  (invoices) =>
    sumMoney(
      invoices.map((invoice) => ({
        amount: invoice.taxPayable || 0,
        currency: invoice.currency,
      })),
    ),
);

export const selectTaxType = createSelector(
  selectPaymentInvoices,
  (invoices) => {
    const firstTaxType = invoices.find((invoice) => invoice.taxType !== '');
    return firstTaxType ? firstTaxType.taxType : '';
  },
);

export const selectPaymentTotalWithoutSurcharge = createSelector(
  selectPaymentInvoices,
  (invoices) =>
    sumMoney(
      invoices.map((invoice) => ({
        amount: invoice.amountPayable || 0,
        currency: invoice.currency,
      })),
    ),
);
export const selectPaymentWidgetSurcharge = (state: StoreState) =>
  state.paymentWidgetSurcharge;

export const selectPaymentSurcharge = createSelector(
  selectPaymentTotalWithoutSurcharge,
  selectPaymentWidgetSurcharge,
  (totalWithoutSurcharge, paymentWidgetSurcharge) => {
    if (paymentWidgetSurcharge) {
      // undefined in case of old widget, zero if new widget no surcharge, > 0 if new widget surcharge
      return {
        ...totalWithoutSurcharge,
        amount: paymentWidgetSurcharge.amount,
      };
    }

    return null;
  },
);

export const selectPaymentTotal = createSelector(
  selectPaymentTotalWithoutSurcharge,
  selectPaymentSurcharge,
  (totalWithoutSurcharge, surcharge) =>
    sumMoney([totalWithoutSurcharge, surcharge]),
);

const selectPaymentSeekBillingAccountIdValue = (state: StoreState) =>
  state.paymentSeekBillingAccountId;

/**
 * paymentSeekBillingAccountId
 * The billing id to use when making a payment. Can be
 * specified by a query parameter or from the currently selected account.
 **/
export const selectPaymentSeekBillingAccountId = createSelector(
  selectSeekBillingAccountId,
  selectPaymentSeekBillingAccountIdValue,
  (currentSeekBillingAccountId, paymentSeekBillingAccountId) =>
    paymentSeekBillingAccountId || currentSeekBillingAccountId,
);

/**
 * isPaymentForThisAccount
 * A payment is made for the currently selected account if the payment does
 * not specifify an account or, is the same as the currently selected one
 **/
const selectIsPaymentForThisAccount = createSelector(
  selectSeekBillingAccountId,
  selectPaymentSeekBillingAccountIdValue,
  (currentSeekBillingAccountId, paymentSeekBillingAccountIdValue) =>
    !paymentSeekBillingAccountIdValue ||
    paymentSeekBillingAccountIdValue === currentSeekBillingAccountId,
);

export const selectRequestedPaymentRequestNumber = (state: StoreState) =>
  state.requestedPaymentRequestNumber;

export const selectIsAdministratorForThisPayment = createSelector(
  selectFeatures,
  selectIsAdministrator,
  selectIsPaymentForThisAccount,
  selectRequestedPaymentRequestNumber,
  (features, isAdmin, isForAccount, requestedPRN) =>
    Boolean(
      (!requestedPRN || features.allowAdministrationOfLinkedPayment) &&
        isAdmin &&
        isForAccount,
    ),
);

export const selectPaymentSource = createSelector(
  selectRequestedPaymentRequestNumber,
  (prn?: Maybe<string>): PaymentSource => {
    if (!prn) {
      return paymentSources.WEB;
    }
    if (prn.match(/^MUL/i)) {
      return paymentSources.CRM;
    }
    if (prn.match(/^INV/i)) {
      return paymentSources.EMAIL;
    }
    return paymentSources.UNKNOWN;
  },
);

export const selectIsTooManyInvoicesSelected = createSelector(
  selectPaymentInvoices,
  (invoices) => invoices.length > MAXIMUM_INVOICES_IN_SINGLE_PAYMENT,
);
